import Modal from '@components/Modal'
import { formatMoney } from '@lib/money'
import { formatInstant, unixToDate } from '@lib/datetime'
import { getShortName } from '@/utils/userNames'
import { GetPayUpType, GetUserType } from '@orbit/api-types'
import React, { useState } from 'react'
import {
    ActionButton,
    Amount,
    AmountRow,
    AuditInfo,
    AuditLine,
    CurrencyCode,
    DateRow,
    DetailsBadge,
    FooterActions,
    HeaderArrow,
    HeaderLabel,
    HeaderName,
    HeaderPerson,
    HeaderRow,
    ImageOverlay,
    Thumbnail,
    ThumbnailGrid,
} from './PayUpDetailsModal.styles'
import { backgroundOrange } from '@/theme/colors'
import { Avatar } from '@components/Avatar'

interface PayUpDetailsModalProps {
    isOpen: boolean
    onClose: () => void
    onDelete?: () => void
    payUp: GetPayUpType | null
    members: GetUserType[]
    currentUserId: number | undefined
    fileUrlById: Map<string, string>
    showCurrencyCode: boolean
}

const formatAmount = formatMoney

const formatDate = (unixSeconds: number): string => formatInstant(unixToDate(unixSeconds), 'dateLong')

const PayUpDetailsModal: React.FC<PayUpDetailsModalProps> = ({
    isOpen,
    onClose,
    onDelete,
    payUp,
    members,
    currentUserId,
    fileUrlById,
    showCurrencyCode,
}) => {
    const [overlayUrl, setOverlayUrl] = useState<string | null>(null)

    if (!payUp) return null

    const fromMember = members.find(m => m.id === payUp.fromUserId)
    const toMember = members.find(m => m.id === payUp.toUserId)
    const createdByMember = members.find(m => m.id === payUp.createdByUserId)
    const lastUpdatedByMember = members.find(m => m.id === payUp.lastUpdatedByUserId)
    const isCurrentUserParticipant =
        currentUserId != null &&
        (payUp.fromUserId === currentUserId || payUp.toUserId === currentUserId)

    const wasEdited = payUp.lastUpdatedAt !== payUp.timestamp
    const fileUrls = (payUp.fileIds ?? [])
        .map(id => fileUrlById.get(id))
        .filter((url): url is string => !!url)

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Payup Details"
            size="medium"
            backgroundColor={backgroundOrange}
            footer={
                onDelete && isCurrentUserParticipant ? (
                    <FooterActions>
                        <ActionButton type="button" variant="danger" onClick={onDelete}>
                            Delete
                        </ActionButton>
                    </FooterActions>
                ) : null
            }
        >
            {!isCurrentUserParticipant && (
                <DetailsBadge>Recorded payup</DetailsBadge>
            )}

            <HeaderRow>
                <HeaderPerson>
                    <HeaderLabel>From</HeaderLabel>
                    <Avatar size="xl" user={fromMember} />
                    <HeaderName>{getShortName(fromMember)}</HeaderName>
                </HeaderPerson>
                <HeaderArrow>→</HeaderArrow>
                <HeaderPerson>
                    <HeaderLabel>To</HeaderLabel>
                    <Avatar size="xl" user={toMember} />
                    <HeaderName>{getShortName(toMember)}</HeaderName>
                </HeaderPerson>
            </HeaderRow>

            <AmountRow>
                <Amount>{formatAmount(payUp.amount, payUp.currencyCode)}</Amount>
                {showCurrencyCode && (
                    <CurrencyCode>{payUp.currencyCode.toUpperCase()}</CurrencyCode>
                )}
            </AmountRow>

            <DateRow>{formatDate(payUp.payUpDate)}</DateRow>

            {fileUrls.length > 0 && (
                <ThumbnailGrid>
                    {fileUrls.map(url => (
                        <Thumbnail key={url} onClick={() => setOverlayUrl(url)}>
                            <img src={url} alt="Receipt" />
                        </Thumbnail>
                    ))}
                </ThumbnailGrid>
            )}

            <AuditInfo>
                <AuditLine>
                    Recorded by <strong>{getShortName(createdByMember)}</strong>
                </AuditLine>
                {wasEdited && (
                    <AuditLine>
                        Last updated by <strong>{getShortName(lastUpdatedByMember)}</strong> on{' '}
                        {formatDate(payUp.lastUpdatedAt)}
                    </AuditLine>
                )}
            </AuditInfo>

            {overlayUrl && (
                <ImageOverlay onClick={() => setOverlayUrl(null)}>
                    <img src={overlayUrl} alt="Receipt full size" />
                </ImageOverlay>
            )}
        </Modal>
    )
}

export default PayUpDetailsModal
